import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { Spinner } from '../../components/ui/Spinner';
import { listAccounts } from '../accounts/api';
import { copySetup, type CopyResponse } from './api';

// "COPY SETUP FROM A CLUB…" (club onboarding, admin only). Pick a source club, tick target clubs, give each a time zone, run a DRY RUN
// (writes nothing), read it, then Apply. ⚠️ The server plans again on apply and writes only the targets with no refusal, so what is
// shown here is a preview, never the authority. Nothing in the report is a club's own text: counts, labels and reasons only.

export const COPY_TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'Australia/Melbourne', label: 'Melbourne' },
  { value: 'Australia/Brisbane', label: 'Brisbane' },
  { value: 'Australia/Adelaide', label: 'Adelaide' },
  { value: 'Australia/Perth', label: 'Perth' },
  { value: 'Australia/Darwin', label: 'Darwin' },
  { value: 'Australia/Hobart', label: 'Hobart' },
  { value: 'Pacific/Auckland', label: 'Auckland' },
];

const selectClass = 'rounded-md border border-slate-300 bg-white p-1.5 text-sm';

export function CopySetupPanel() {
  const accounts = useQuery({ queryKey: ['kyckconnect', 'copy-accounts'], queryFn: listAccounts });
  const [from, setFrom] = useState('');
  const [picked, setPicked] = useState<Record<string, string>>({}); // accountId -> time zone
  const [filter, setFilter] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [result, setResult] = useState<CopyResponse | null>(null);

  const clubs = useMemo(
    () => (accounts.data ?? []).filter((a) => a.accountType === 'location').sort((a, b) => a.name.localeCompare(b.name)),
    [accounts.data],
  );
  const visible = clubs.filter((c) => c.id !== from && c.name.toLowerCase().includes(filter.trim().toLowerCase()));
  const targets = Object.entries(picked).filter(([id]) => id !== from).map(([accountId, timezone]) => ({ accountId, timezone }));

  const run = useMutation({
    mutationFn: (apply: boolean) => copySetup(from, targets, overwrite, apply),
    onSuccess: setResult,
  });

  const toggle = (id: string, on: boolean) =>
    setPicked((p) => {
      const next = { ...p };
      if (on) next[id] = next[id] ?? 'Australia/Sydney';
      else delete next[id];
      return next;
    });

  return (
    <section className="space-y-4 rounded-md border border-slate-200 bg-white p-4" data-testid="copy-setup">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Copy setup from a club</h2>
        <p className="text-sm text-slate-600">
          Copies the wording, offers, follow-ups and bot settings. Address, phone, email, every link, videos and the team are left blank for the club to fill.
          Everything copied is marked “check before going live”, and the new bot starts in Setting up.
        </p>
      </div>
      {accounts.isLoading && <Spinner />}
      <div className="max-w-sm">
        <label className="mb-1 block text-sm text-slate-700" htmlFor="copy-from">Copy from</label>
        <Combobox
          id="copy-from"
          value={from}
          onValueChange={(v) => { setFrom(v); setResult(null); }}
          options={clubs.map((c) => ({ value: c.id, label: c.name }))}
          placeholder="Pick the source club"
          searchPlaceholder="Search clubs"
          emptyText="No club found"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm text-slate-700" htmlFor="copy-filter">Copy to (tick each club and pick its time zone)</label>
        <input id="copy-filter" className="w-full max-w-sm rounded-md border border-slate-300 p-1.5 text-sm" placeholder="Filter clubs" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <ul className="max-h-60 space-y-1 overflow-y-auto rounded border border-slate-200 p-2" data-testid="copy-targets">
          {visible.slice(0, 200).map((c) => (
            <li key={c.id} className="flex items-center gap-2 text-sm">
              <input id={`copy-${c.id}`} type="checkbox" checked={c.id in picked} onChange={(e) => toggle(c.id, e.target.checked)} />
              <label htmlFor={`copy-${c.id}`} className="flex-1">{c.name}</label>
              {c.id in picked && (
                <select className={selectClass} aria-label={`Time zone for ${c.name}`} value={picked[c.id]} onChange={(e) => setPicked((p) => ({ ...p, [c.id]: e.target.value }))}>
                  {COPY_TIMEZONES.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
                </select>
              )}
            </li>
          ))}
        </ul>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={overwrite} onChange={(e) => { setOverwrite(e.target.checked); setResult(null); }} />
        Replace what a club already has (its profile, offers and bot setup). Without this, a club that already has any of them is refused.
      </label>

      <div className="flex gap-2">
        <Button variant="outline" disabled={!from || targets.length === 0 || run.isPending} onClick={() => run.mutate(false)}>Dry run (writes nothing)</Button>
        <Button disabled={!result || !result.dryRun || run.isPending || result.reports.every((r) => r.plan.refusals.length > 0)} onClick={() => run.mutate(true)}>
          Apply to the clubs with no refusal
        </Button>
      </div>
      {run.isPending && <Spinner />}
      {run.error && <p role="alert" className="text-sm text-red-700">{run.error instanceof Error ? run.error.message : 'The copy failed.'}</p>}

      {result && (
        <div className="space-y-3" data-testid="copy-result">
          <p className="text-sm font-medium text-slate-800">{result.dryRun ? 'Dry run: nothing was written.' : 'Applied.'} Source: {result.source.name}.</p>
          {result.reports.map((r) => (
            <div key={r.accountId} className="space-y-1 rounded border border-slate-200 p-3 text-sm" data-testid={`copy-report-${r.accountId}`}>
              <p className="font-medium text-slate-900">{r.accountName}</p>
              {r.plan.refusals.length > 0 && (
                <ul className="list-disc pl-5 text-red-700">{r.plan.refusals.map((x, i) => <li key={i}>{x}</li>)}</ul>
              )}
              <p>Name changes: {r.plan.substitutions.total} ({r.plan.substitutions.fullName} club name, {r.plan.substitutions.suburb} suburb).</p>
              {r.plan.residuals.length > 0 && <p className="text-red-700">Source details still present in {r.plan.residuals.length} place(s): {r.plan.residuals.map((x) => `${x.path} (${x.token})`).join(', ')}.</p>}
              <p>Club must fill: {r.plan.mustFill.length === 0 ? 'nothing' : r.plan.mustFill.map((m) => `${m.section} (${m.fields.join(', ')})`).join('; ')}.</p>
              <p>To check before going live: {r.plan.needsReview.length === 0 ? 'nothing' : r.plan.needsReview.join('; ')}.</p>
              <p>{r.plan.calendar.note}</p>
              <p>{r.plan.number.poolNumber ? 'Texts from the group’s shared number.' : 'No shared number found for this club.'}</p>
              {r.applied.length > 0 && <ul className="list-disc pl-5 text-green-800">{r.applied.map((x, i) => <li key={i}>{x}</li>)}</ul>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
